import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';

const OverviewSection = ({ trainerId, refreshKey, onPlanSelect, unreadMessagesCount }) => {
  const [stats, setStats] = useState({
    totalClients: 0,
    activePlans: 0,
    taskCompletionRate: 0,
    averageSessionScore: 0
  });
  const [activities, setActivities] = useState({
    completions: [],
    ratings: [],
    videos: []
  });
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchOverviewData = useCallback(async () => {
    try {
      if (!trainerId) return;

      const { data: clients } = await supabase.from('training_clients').select('id').eq('trainer_id', trainerId);
      const clientIds = clients?.map(c => c.id) || [];
      if (clientIds.length === 0) {
        setLoading(false);
        return;
      }

      const { data: plans } = await supabase.from('training_plans').select('id').in('client_id', clientIds);
      const planIds = plans?.map(p => p.id) || [];

      const [videosRes, ratingsRes, completionsRes, statsRes] = await Promise.all([
        supabase
          .from('training_tasks')
          .select(`*, training_plans(id, plan_name, training_clients(dog_name))`)
          .in('plan_id', planIds)
          .not('video_submission_url', 'is', null)
          .neq('video_submission_url', '')
          .order('video_reviewed', { ascending: true })
          .order('updated_at', { ascending: false })
          .limit(15),
        supabase
          .from('training_tasks')
          .select(`*, training_plans(id, plan_name, training_clients(dog_name))`)
          .in('plan_id', planIds)
          .not('session_score', 'is', null)
          .order('updated_at', { ascending: false })
          .limit(10),
        supabase
          .from('training_tasks')
          .select(`*, training_plans(id, plan_name, training_clients(dog_name))`)
          .in('plan_id', planIds)
          .eq('is_completed', true)
          .order('updated_at', { ascending: false })
          .limit(10),
        supabase
          .from('training_tasks')
          .select('id, is_completed, session_score', { count: 'exact' })
          .in('plan_id', planIds)
      ]);

      const allTasks = statsRes.data || [];
      const completedCount = allTasks.filter(t => t.is_completed).length;
      const scoredTasks = allTasks.filter(t => t.session_score);
      const avgScore = scoredTasks.length > 0 ? (scoredTasks.reduce((s, t) => s + t.session_score, 0) / scoredTasks.length).toFixed(1) : 0;

      setStats({
        totalClients: clientIds.length,
        activePlans: planIds.length,
        taskCompletionRate: allTasks.length > 0 ? Math.round((completedCount / allTasks.length) * 100) : 0,
        averageSessionScore: avgScore
      });

      setActivities({
        videos: videosRes.data || [],
        ratings: ratingsRes.data || [],
        completions: completionsRes.data || []
      });
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  }, [trainerId]);

  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData, refreshKey]);

  const handleToggleReviewed = async (taskId, currentStatus) => {
    setUpdatingId(taskId);
    try {
      await supabase.from('training_tasks').update({ video_reviewed: !currentStatus }).eq('id', taskId);
      await fetchOverviewData();
    } finally {
      setUpdatingId(null);
    }
  };

  const ActivityColumn = ({ title, icon, data, type, colorClass, iconColor }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${iconColor.replace('text', 'bg')}/10`}>
            <SafeIcon name={icon} className={`w-5 h-5 ${iconColor}`} />
          </div>
          <h3 className="font-bold text-gray-800">{title}</h3>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto max-h-[500px]">
        {data.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm italic">No recent {title.toLowerCase()}</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {data.map((item) => (
              <div key={item.id} className={`p-4 hover:bg-gray-50/80 transition-colors group relative ${type === 'video' && item.video_reviewed ? 'opacity-60' : ''}`}>
                <div className="flex justify-between items-start mb-1.5">
                  <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{item.training_plans?.training_clients?.dog_name}</span>
                  <span className="text-[10px] text-gray-400 font-medium">{format(new Date(item.updated_at), 'MMM d, p')}</span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-bold text-gray-900 line-clamp-1 mb-1">{item.title}</p>
                  {type === 'video' && (
                    <div className="flex gap-1">
                      {/* DIRECT LINK TO VIDEO */}
                      <a 
                        href={item.video_submission_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-1.5 bg-indigo-50 text-indigo-600 rounded-md border border-indigo-100 hover:bg-indigo-100 transition-colors"
                        title="Watch Video"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SafeIcon name="Video" className="w-4 h-4" />
                      </a>
                      <button 
                        onClick={() => handleToggleReviewed(item.id, item.video_reviewed)} 
                        disabled={updatingId === item.id}
                        className={`flex-shrink-0 p-1 rounded-md border transition-all ${item.video_reviewed ? 'bg-green-50 border-green-200 text-green-600' : 'bg-white border-gray-200 text-gray-400 hover:border-indigo-300 hover:text-indigo-500'}`}
                        title="Mark Reviewed"
                      >
                        {updatingId === item.id ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <SafeIcon name={item.video_reviewed ? "CheckSquare" : "Square"} className="w-4 h-4" />}
                      </button>
                    </div>
                  )}
                </div>
                {type === 'rating' && (
                  <div className="flex gap-0.5 mb-2">
                    {[...Array(5)].map((_, i) => <SafeIcon key={i} name="Star" className={`w-3.5 h-3.5 ${i < item.session_score ? 'text-yellow-400 fill-current' : 'text-gray-200'}`} />)}
                  </div>
                )}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-dashed border-gray-100">
                  <span className="text-[10px] text-gray-500 font-medium truncate max-w-[140px]">{item.training_plans?.plan_name}</span>
                  <button onClick={() => onPlanSelect(item.training_plans, item.id)} className={`text-[11px] font-bold flex items-center gap-1 py-1 px-2.5 rounded-lg transition-all border border-transparent shadow-sm ${colorClass} hover:shadow-md`}>
                    Go to Task <SafeIcon name="ArrowRight" className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) return <div className="space-y-8 animate-pulse"><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-white rounded-xl border border-gray-200" />)}</div></div>;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Clients" value={stats.totalClients} icon="Users" color="blue" />
        <StatCard title="Active Plans" value={stats.activePlans} icon="Calendar" color="green" />
        <StatCard title="Completion" value={`${stats.taskCompletionRate}%`} icon="CheckCircle" color="purple" />
        <StatCard title="Avg Score" value={stats.averageSessionScore} icon="Star" color="yellow" isScore />
        <StatCard title="Unread Messages" value={unreadMessagesCount} icon="MessageCircle" color="indigo" isAlert={unreadMessagesCount > 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <ActivityColumn title="Video Submissions" icon="Video" data={activities.videos} type="video" colorClass="bg-blue-600 text-white hover:bg-blue-700" iconColor="text-blue-600" />
        <ActivityColumn title="Session Ratings" icon="Star" data={activities.ratings} type="rating" colorClass="bg-yellow-500 text-white hover:bg-yellow-600" iconColor="text-yellow-600" />
        <ActivityColumn title="Task Completions" icon="CheckSquare" data={activities.completions} type="completion" colorClass="bg-green-600 text-white hover:bg-green-700" iconColor="text-green-600" />
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color, isScore, isAlert }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    indigo: 'bg-indigo-50 text-indigo-600'
  };

  return (
    <div className={`bg-white p-6 rounded-xl shadow-sm border ${isAlert ? 'border-indigo-300 ring-1 ring-indigo-100' : 'border-gray-200'} flex items-center justify-between transition-all`}>
      <div>
        <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
        <div className="flex items-center gap-1.5">
          <p className={`text-2xl font-bold ${isAlert ? 'text-indigo-600' : 'text-gray-900'}`}>{value}</p>
          {isScore && <SafeIcon name="Star" className="w-5 h-5 text-yellow-400 fill-current" />}
        </div>
      </div>
      <div className={`p-3 rounded-xl ${colors[color]} ${isAlert ? 'animate-pulse' : ''}`}>
        <SafeIcon name={icon} className="w-6 h-6" />
      </div>
    </div>
  );
};

export default OverviewSection;